import { paginateNdtGlobalItems, type NdtGlobalListQuery, type NdtGlobalListResult } from "../../domain/ndt/ndt-global-list-query.js";
import type { RequestContext } from "../context/request-context.js";
import type { LpConsumableRepository } from "../../repositories/operational-repositories.js";
import {
  type LpConsumable,
  type LpConsumableStatus,
  type LpConsumableType,
  selectableLpConsumables,
  validateLpConsumable
} from "../../domain/ndt/lp-consumable.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import { RbacAuthorizationService } from "../../permissions/authorization.js";
import { PermissionCode } from "../../permissions/permissions.js";

const randomUUID = () => globalThis.crypto.randomUUID();

/** Diz se um consumível já está apontado por algum relatório LP — vinculado nunca é apagado, só inativado. */
export type LpConsumableUsageChecker = (consumableId: string) => Promise<boolean>;

/**
 * Cadastro global END → Consumíveis LP. Mesmas permissões do restante do
 * cadastro de END (NdtRegistry*): é dado mestre da empresa inspetora.
 */
export class LpConsumableService {
  constructor(
    private readonly repo: LpConsumableRepository,
    private readonly isInUse?: LpConsumableUsageChecker
  ) {}

  /** M21: limite 100 no backend, busca global sem escopo de Cliente. */
  async listPaged(context: RequestContext, input: NdtGlobalListQuery): Promise<NdtGlobalListResult<LpConsumable>> {
    new RbacAuthorizationService().require(context, PermissionCode.NdtRegistryRead);
    const repository = this.repo as unknown as {
      listPaged?: (query: NdtGlobalListQuery) => Promise<NdtGlobalListResult<LpConsumable>>;
    };
    if (repository.listPaged) return repository.listPaged(input);
    return paginateNdtGlobalItems(await this.repo.listAll(), input);
  }

  async list(context: RequestContext, type?: LpConsumableType): Promise<LpConsumable[]> {
    new RbacAuthorizationService().require(context, PermissionCode.NdtRegistryRead);
    const all = await this.repo.listAll();
    const filtered = type ? all.filter(i => i.type === type) : all;
    return [...filtered].sort((a, b) =>
      a.type.localeCompare(b.type) || a.brand.localeCompare(b.brand, "pt-BR") || a.product.localeCompare(b.product, "pt-BR")
    );
  }

  async getById(context: RequestContext, id: string): Promise<LpConsumable | null> {
    new RbacAuthorizationService().require(context, PermissionCode.NdtRegistryRead);
    return this.repo.findById(id);
  }

  /** O que o relatório oferece: ATIVOS do tipo, mais o que ele já aponta. */
  async listSelectableForReport(
    context: RequestContext,
    type: LpConsumableType,
    currentlyLinkedId?: string | null
  ): Promise<LpConsumable[]> {
    new RbacAuthorizationService().require(context, PermissionCode.NdtRegistryRead);
    const all = await this.repo.listAll();
    return selectableLpConsumables(all, type, currentlyLinkedId);
  }

  async create(context: RequestContext, data: Partial<LpConsumable>): Promise<LpConsumable> {
    new RbacAuthorizationService().require(context, PermissionCode.NdtRegistryCreate);

    const errors = validateLpConsumable(data);
    if (errors.length > 0) {
      throw new AppError(ErrorCode.Validation, `Invalid LP consumable: ${errors.join(" ")}`, {
        safeMessage: errors.join(" ")
      });
    }

    await this.assertLotIsUnique(data.type as LpConsumableType, data.brand!.trim(), data.product!.trim(), data.lot!.trim(), null);

    const now = new Date().toISOString();
    const item: LpConsumable = {
      id: `LPC-${randomUUID()}`,
      type: data.type as LpConsumableType,
      brand: data.brand!.trim(),
      product: data.product!.trim(),
      lot: data.lot!.trim(),
      validity: (data.validity || "").trim(),
      status: (data.status as LpConsumableStatus) || "ACTIVE",
      observations: data.observations || "",
      createdAt: now,
      updatedAt: now,
      createdBy: context.userId || "system",
      updatedBy: context.userId || "system"
    };

    await this.repo.save(item);
    return item;
  }

  async update(context: RequestContext, id: string, data: Partial<LpConsumable>): Promise<LpConsumable> {
    new RbacAuthorizationService().require(context, PermissionCode.NdtRegistryUpdate);

    const existing = await this.requireItem(id);
    const merged: LpConsumable = {
      ...existing,
      ...data,
      id: existing.id,
      createdAt: existing.createdAt,
      createdBy: existing.createdBy
    };

    const errors = validateLpConsumable(merged);
    if (errors.length > 0) {
      throw new AppError(ErrorCode.Validation, `Invalid LP consumable: ${errors.join(" ")}`, {
        safeMessage: errors.join(" ")
      });
    }

    await this.assertLotIsUnique(merged.type, merged.brand.trim(), merged.product.trim(), merged.lot.trim(), existing.id);

    const updated: LpConsumable = {
      ...merged,
      brand: merged.brand.trim(),
      product: merged.product.trim(),
      lot: merged.lot.trim(),
      validity: (merged.validity || "").trim(),
      updatedAt: new Date().toISOString(),
      updatedBy: context.userId || "system"
    };

    await this.repo.save(updated);
    return updated;
  }

  async setStatus(context: RequestContext, id: string, status: LpConsumableStatus): Promise<LpConsumable> {
    new RbacAuthorizationService().require(context, PermissionCode.NdtRegistryInactivate);
    const existing = await this.requireItem(id);
    const updated: LpConsumable = {
      ...existing,
      status,
      updatedAt: new Date().toISOString(),
      updatedBy: context.userId || "system"
    };
    await this.repo.save(updated);
    return updated;
  }

  async delete(context: RequestContext, id: string): Promise<void> {
    new RbacAuthorizationService().require(context, PermissionCode.NdtRegistryDelete);
    const existing = await this.requireItem(id);

    if (this.isInUse && await this.isInUse(existing.id)) {
      throw new AppError(ErrorCode.Conflict, "LP consumable is referenced by a report", {
        safeMessage: `O consumível "${existing.brand} — ${existing.product} (Lote ${existing.lot})" já está vinculado a um Relatório LP e não pode ser excluído. Utilize a ação de inativar.`
      });
    }

    await this.repo.delete(existing.id);
  }

  private async requireItem(id: string): Promise<LpConsumable> {
    const existing = await this.repo.findById(id);
    if (!existing) {
      throw new AppError(ErrorCode.NotFound, "LP consumable not found", {
        safeMessage: "Consumível LP não encontrado."
      });
    }
    return existing;
  }

  /** O mesmo lote do mesmo produto cadastrado duas vezes confundiria a rastreabilidade do ensaio. */
  private async assertLotIsUnique(type: LpConsumableType, brand: string, product: string, lot: string, ignoreId: string | null): Promise<void> {
    const all = await this.repo.listAll();
    const duplicate = all.find(i =>
      i.id !== ignoreId &&
      i.type === type &&
      i.brand.trim().toLowerCase() === brand.toLowerCase() &&
      i.product.trim().toLowerCase() === product.toLowerCase() &&
      i.lot.trim().toLowerCase() === lot.toLowerCase()
    );
    if (duplicate) {
      throw new AppError(ErrorCode.Conflict, "Duplicated LP consumable lot", {
        safeMessage: `Já existe o lote "${lot}" cadastrado para ${brand} — ${product}.`
      });
    }
  }
}
